import React, { useEffect, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import { Box, Button, IconButton, Tooltip, Chip, Paper, Typography } from '@mui/material';
import { Delete, Edit, Add, AutoAwesome, Visibility, Print } from '@mui/icons-material';

import CustomDialog from '../../../layout/CustomDialog';
import ResponsiveToolbarActions from '../../../layout/ResponsiveToolbarActions';
import HomologationItemDialog from './HomologationItemDialog';
import HomologationItemDetailPanel from './HomologationItemDetailPanel';

import { printPdf } from '../../../../utils/funcoes';
import { retornaDataBr } from '../../../../utils/retornaDataFormatada';
import { getStatusPrazo, getResumoStatusItens } from '../utils/homologationStatus';

import * as HomologationAPI from '../../../../api/homologation';

import {
    podeImprimir,
    podeIncluir,
    podeEditar,
    podeCancelar,
    podeImprimirGerencia,
    podeImprimirAnalitico,
} from '../../../../hooks/usePermisionMRT';

function HomologationItemDetailLoader({
    item,
    detail,
    loading,
    fetchDetailPanel,
    pessoaId,
    isResponsavelHomologacao,
    onAssinar,
    onRemoverAssinatura,
    onRemoverPessoaDoItem,
    onOpenRemoverPessoaDialog,
}) {
    useEffect(() => {
        if (!item?.id) return;
        if (detail) return;
        if (loading) return;

        fetchDetailPanel(item.id);
    }, [item?.id, detail, loading, fetchDetailPanel]);

    return (
        <HomologationItemDetailPanel
            item={item}
            detail={detail}
            loading={loading}
            pessoaId={pessoaId}
            isResponsavelHomologacao={isResponsavelHomologacao}
            onAssinar={onAssinar}
            onRemoverAssinatura={onRemoverAssinatura}
            onOpenRemoverPessoaDialog={onOpenRemoverPessoaDialog}
        />
    );
}

function CardResumo({ label, value, color = 'default' }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.2,
                minWidth: 140,
                borderRadius: 1.5,
            }}
        >
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                {label}
            </Typography>
            <Chip label={value} color={color} size="small" />
        </Paper>
    );
}

function ItensTabPanel({ module }) {
    const { state, actions, context, dialogs, mrt, options } = module;

    const resumo = useMemo(() => getResumoStatusItens(state.rowsResumo || []), [state.rowsResumo]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'descricao',
                header: 'Descrição',
                muiTableBodyCellProps: ({ row }) => ({
                    onClick: () => {
                        console.info(row.original);
                    },
                }),
            },
            {
                accessorKey: 'Menu.descricao',
                header: 'Menu',
                columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
                filterVariant: 'select',
                filterSelectOptions: options.optionsMenuFilter || [],
            },
            {
                accessorKey: 'menuSub.descricao',
                header: 'SubMenu',
                columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
                filterVariant: 'select',
                filterSelectOptions: options.optionsMenuSubFilter || [],
            },
            {
                accessorKey: 'Situacao.descricao',
                header: 'Situação',
                filterFn: 'equals',
                columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
                filterVariant: 'select',
                filterSelectOptions: options.optionsSituacaoFilter || [],
            },
            {
                accessorKey: 'Operacao.descricao',
                header: 'Operação',
                columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
                filterVariant: 'select',
                filterSelectOptions: options.optionsOperacaoFilter || [],
            },
            {
                id: 'statusPrazo',
                header: 'Status',
                muiTableBodyCellProps: ({ row }) => {
                    const status = getStatusPrazo(row.original);

                    if (status.label === 'Atrasado') {
                        return {
                            sx: { backgroundColor: 'rgba(244, 67, 54, 0.08)' },
                        };
                    }

                    if (status.label === 'Homologado com atraso') {
                        return {
                            sx: { backgroundColor: 'rgba(255, 152, 0, 0.08)' },
                        };
                    }

                    return {};
                },
                Cell: ({ row }) => {
                    const status = getStatusPrazo(row.original);
                    return <Chip label={status.label} color={status.color} size="small" />;
                },
            },
            {
                accessorKey: 'dataInicio',
                header: 'Início',
                Cell: ({ cell }) => (cell.getValue() ? retornaDataBr(cell.getValue(), 'date') : ''),
            },
            {
                accessorKey: 'dataFim',
                header: 'Fim',
                Cell: ({ cell }) => (cell.getValue() ? retornaDataBr(cell.getValue(), 'date') : ''),
            },
            {
                accessorKey: 'dataHomologado',
                header: 'Homologado em',
                Cell: ({ cell }) => (cell.getValue() ? retornaDataBr(cell.getValue(), 'date') : ''),
            },
            { accessorKey: 'versaoFrontHomologada', header: 'Front' },
            { accessorKey: 'versaoBackHomologada', header: 'Back' },
        ],
        [options.optionsMenuFilter, options.optionsMenuSubFilter, options.optionsSituacaoFilter, options.optionsOperacaoFilter]
    );

    //função do toolbar
    // Handlers específicos da tela
    const imprimirItensGeral = async (obj) => {
        const response = await HomologationAPI.imprimirItensGeral(obj);
        printPdf(response.data);
    };

    const imprimirItemDetalhe = async (obj) => {
        const response = await HomologationAPI.imprimirItemDetalhe(obj);
        printPdf(response.data);
    };

    return (
        <div>
            {dialogs.openDeleteDialog && (
                <CustomDialog
                    title="Delete ITEM!"
                    content="Você tem certeza?"
                    onClose={actions.handleCancelar}
                    onConfirm={actions.handleDeletar}
                />
            )}

            {dialogs.openDialog && (
                <HomologationItemDialog
                    open={dialogs.openDialog}
                    draft={dialogs.draft}
                    options={options}
                    onChange={actions.handleChangeDraft}
                    onSalvar={actions.handleSalvarDraft}
                    onCancelar={actions.handleCancelar}
                    actions={actions}
                    context={context}
                />
            )}
            {dialogs.openGerarDialog && (
                <CustomDialog
                    title="Gerar ITENS da homologação"
                    content="Deseja realmente gerar os itens automaticamente?"
                    onClose={actions.handleCloseGerarDialog}
                    onConfirm={actions.handleConfirmGerarItens}
                />
            )}

            {dialogs.openRemoverPessoaDialog && (
                <CustomDialog
                    title="Remover Pessoa do Item"
                    content="Deseja realmente remover esta pessoa somente deste item?"
                    onClose={actions.handleCloseRemoverPessoaDialog}
                    onConfirm={actions.handleRemoverPessoaDoItem}
                />
            )}
            <MaterialReactTable
                columns={columns}
                data={state.rows}
                enableRowSelection
                enableRowActions
                getRowId={(row) => row.uuid}
                manualFiltering
                manualPagination
                manualSorting
                enableColumnFilterModes
                enableGlobalFilterModes
                enableColumnOrdering
                localization={MRT_Localization_PT_BR}
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        muiTableHeadCellProps: {
                            align: 'center',
                        },
                        size: 100,
                    },
                }}
                renderTopToolbarCustomActions={({ table }) => {
                    const action = [
                        {
                            key: 'adicionar',
                            label: 'Adicionar',
                            tooltip: 'nono itens a homologação',
                            startIcon: <Add />,
                            color: 'secondary',
                            onClick: actions.handleAdicionar,
                            disabled: !context?.isResponsavelHomologacao,
                        },
                        {
                            key: 'gerar',
                            label: 'Gerar',
                            tooltip: 'gerar todos os itens da homologação com todos os usuários que possuem permissão',
                            startIcon: <AutoAwesome />,
                            color: 'success',
                            onClick: actions.handleOpenGerarDialog,
                            disabled: state.gerandoItens || !context?.isResponsavelHomologacao,
                        },
                        {
                            key: 'imprimir',
                            label: 'Imprimir',
                            tooltip: 'visualizar itens da homologação',
                            startIcon: <Visibility />,
                            color: 'info',
                            onClick: () => imprimirItensGeral({ homologationId: state.homologationId }),
                            disabled: !podeImprimirAnalitico(),
                        },
                    ].filter((a) => a?.show ?? true);
                    return <ResponsiveToolbarActions actions={action} />;
                }}
                renderRowActions={({ row, table }) => {
                    return (
                        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                            {context?.isResponsavelHomologacao ? (
                                <Tooltip title="Editar">
                                    <IconButton onClick={() => actions.handleEditar(row)}>
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Visualizar objetivo do item">
                                    <IconButton onClick={() => actions.handleVisualizar(row)}>
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title="Deletar">
                                <IconButton
                                    color="error"
                                    disabled={!context?.isResponsavelHomologacao}
                                    onClick={() => {
                                        actions.setSelectedRow(row.original);
                                        actions.setOpenDeleteDialog(true);
                                    }}
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Deletar">
                                <IconButton
                                    color="info"
                                    disabled={!context?.isResponsavelHomologacao}
                                    onClick={() => {
                                        imprimirItemDetalhe(row.original);
                                    }}
                                >
                                    <Print />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }}
                renderDetailPanel={({ row }) => {
                    const itemId = row.original.id;
                    const detail = state.detailByItemId?.[itemId];
                    const loading = state.loadingDetailByItemId?.[itemId];

                    return (
                        <HomologationItemDetailLoader
                            item={row.original}
                            detail={detail}
                            loading={loading}
                            fetchDetailPanel={actions.fetchDetailPanel}
                            pessoaId={context?.pessoaId}
                            isResponsavelHomologacao={context?.isResponsavelHomologacao}
                            onAssinar={actions.handleAssinarItem}
                            onRemoverAssinatura={actions.handleRemoverAssinaturaItem}
                            onOpenRemoverPessoaDialog={actions.handleOpenRemoverPessoaDialog}
                        />
                    );
                }}
                onColumnFiltersChange={mrt.setColumnFilters}
                onColumnFilterFnsChange={mrt.setColumnFiltersMode}
                onGlobalFilterChange={mrt.setGlobalFilter}
                onPaginationChange={mrt.setPagination}
                onSortingChange={mrt.setSorting}
                rowCount={mrt.rowCount}
                initialState={{
                    showColumnFilters: true,
                    density: 'compact',
                }}
                state={{
                    columnFilterFns: mrt.columnFilterFns,
                    columnFilters: mrt.columnFilters,
                    globalFilter: mrt.globalFilter,
                    isLoading: mrt.isLoading,
                    pagination: mrt.pagination,
                    showAlertBanner: mrt.isError,
                    showProgressBars: mrt.isRefetching,
                    sorting: mrt.sorting,
                }}
                muiToolbarAlertBannerProps={
                    mrt.isError
                        ? {
                              color: 'error',
                              children: 'Erro ao carregar itens',
                          }
                        : undefined
                }
                positionActionsColumn="first"
                positionToolbarAlertBanner="bottom"
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <CardResumo label="Total" value={resumo.total} />
                <CardResumo label="Não iniciado" value={resumo.naoIniciado} />
                <CardResumo label="Em andamento" value={resumo.emAndamento} color="info" />
                <CardResumo label="Atrasado" value={resumo.atrasado} color="error" />
                <CardResumo label="Homologado no prazo" value={resumo.homologadoNoPrazo} color="success" />
                <CardResumo label="Homologado com atraso" value={resumo.homologadoComAtraso} color="warning" />
                <CardResumo label="Sem prazo" value={resumo.semPrazo} />
            </Box>
        </div>
    );
}

export default ItensTabPanel;
